import React, {createContext, useContext, useEffect, useState} from "react";
import ApiInterface from "@/commons/api/ApiInterface";
import Cookies from "js-cookie";
import {environment} from "@/commons/configs/Config";
import {getToken} from "@/commons/utils/others/Account";
import {getDetails} from "@/commons/api/apiPages/Account";
import {AccessTokenResponse} from "@/commons/api/objects/responses/auth/AccessTokenResponse";

type StaffGroupType = {
    id: number,
    groupPower: number,
    groupName: string,
    groupDescription: string,
    iconUUID: string,
    staffer: boolean
}

type AccountDataType = {
    id: number,
    uid: string,
    username: string,
    email: string,
    minecraftUid: string,
    twitchId: string,
    staffGroup: StaffGroupType,
    emailVerified: boolean,
    lastKnowIp: string
}

type MinecraftDataType = {
    authCode: string,
    firstLogin: number,
    lastLogout: number,
    pastUsernames: [],
    username: string,
    uuid: string,
}

type TwitchDataType = {
    id: string,
    banned: boolean,
    login: string,
    minecraftId: string,
    subActive: boolean,
    subTier: string,
    vip: boolean,
    telegramId: number,
}

type ReportsDataType = {
    open: number,
    closed: number,
    total: number,
}

type UserContextType = {
    isLogged: boolean;
    isStaff: boolean;
    userLoadingComplete: boolean;
    minecraftData: MinecraftDataType | undefined;
    accountData: AccountDataType | undefined;
    twitchData: TwitchDataType | undefined;
    reportsData: ReportsDataType | undefined;
    newRefreshToken: (token: AccessTokenResponse) => Promise<void>;
};

const UserContext = createContext<UserContextType>({
    isLogged: false,
    isStaff: false,
    userLoadingComplete: false,
    minecraftData: undefined,
    accountData: undefined,
    twitchData: undefined,
    reportsData: undefined,
    newRefreshToken: async () => {
    } // placeholder, verrà sovrascritto nel provider
});

export const useUser = () => useContext(UserContext);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const [isLogged, setIsLogged] = useState(false);
    const [isStaff, setIsStaff] = useState(false);
    const [minecraftData, setMinecraftData] = useState<MinecraftDataType | undefined>(undefined);
    const [accountData, setAccountData] = useState<AccountDataType | undefined>(undefined);
    const [twitchData, setTwitchData] = useState<TwitchDataType | undefined>(undefined);
    const [reportsData, setReportsData] = useState<ReportsDataType | undefined>(undefined);
    const [userLoadingComplete, setUserLoadingComplete] = useState(false);

    const [loadAccessToken, setLoadAccessToken] = useState<String | null>(null);

    let token = getToken();

    async function loadInfo() {
        try {
            const logged = await ApiInterface.checkLogged();
            setIsLogged(logged);

            if (logged) {
                const staff = await ApiInterface.isStaffer();
                setIsStaff(staff);

                await loadUserInfo();
                await loadMinecraftInfo();
                await loadTwitchInfo();
                await loadReportsInfo();
            }
        } catch (err) {
            setIsLogged(false);
            setIsStaff(false);
        } finally {
            setUserLoadingComplete(true);
        }
    }

    async function loadMinecraftInfo() {
        let mcData = await getMinecraft(token);
        if (mcData === null) return;
        if (mcData.status === 200) {
            let json = await mcData.json();

            let converted: MinecraftDataType = {
                authCode: json.authCode ?? "",
                firstLogin: json.firstLogin ?? 0,
                lastLogout: json.lastLogout ?? 0,
                pastUsernames: Array.isArray(json.pastUsernames) ? json.pastUsernames : [],
                username: json.username ?? "",
                uuid: json._id ?? "",
            };
            setMinecraftData(converted);
        }
        return
    }

    async function loadTwitchInfo() {
        let twData = await fetchTwitch(token);
        if (twData === null) return;
        if (twData.status === 200) {
            let json = await twData.json();

            let converted: TwitchDataType = {
                id: json.id ?? "",
                banned: json.banned ?? false,
                login: json.login ?? "",
                minecraftId: json.minecraftId ?? "",
                subActive: json.subActive ?? false,
                subTier: json.tier ?? "",
                vip: json.vip ?? false,
                telegramId: json.telegramId ?? "",
            }

            setTwitchData(converted);
        }
        return
    }

    async function loadReportsInfo() {
        let repData = await fetchReports(token);
        if (repData === null) return;
        if (repData.status === 200) {
            let json = await repData.json();

            let converted: ReportsDataType = {
                open: json.openReports ?? 0,
                closed: json.closeReports ?? 0,
                total: json.totalReports ?? 0,
            }

            setReportsData(converted);
        }

        return
    }

    async function loadUserInfo() {
        let account = await getDetails(token);
        if (account === null) return;
        if (account.status !== 200) return;

        let json = await account.json();
        setAccountData(convertAccountData(json));
        return
    }

    function convertAccountData(json: any): AccountDataType {
        return {
            id: json.id ?? 0,
            uid: json.uid ?? "",
            username: json.username ?? "",
            email: json.email ?? "",
            minecraftUid: json.minecraftUid ?? "",
            twitchId: json.twitchId ?? "",
            staffGroup: {
                id: json.staffGroup?.id ?? 0,
                groupPower: json.staffGroup?.groupPower ?? 0,
                groupName: json.staffGroup?.groupName ?? "",
                groupDescription: json.staffGroup?.groupDescription ?? "",
                iconUUID: json.staffGroup?.iconUUID ?? "",
                staffer: json.staffGroup?.staffer ?? false
            },
            emailVerified: json.emailVerified ?? false,
            lastKnowIp: json.lastKnowIp ?? "",
        };
    }

    function checkCookies() {
        const cookiesAccepted = Cookies.get("cookiesAccepted");
        if (cookiesAccepted !== undefined) {
            Cookies.set('cookiesAccepted', cookiesAccepted, {expires: 30, path: '/'});
        }
    }

    async function newRefreshToken(token: AccessTokenResponse) {
        Cookies.set(environment.accessToken, token.accessToken, {expires: new Date(token.expireDate), path: '/'});

        setAccessTokenTimer();
    }

    let accessTokenTimeout: NodeJS.Timeout | null = null;

    function setAccessTokenTimer() {
        const token = Cookies.get(environment.accessToken);
        if (!token) return;

        const payloadBase64 = token.split('.')[1];
        if (!payloadBase64) return;

        try {
            const decodedPayload = JSON.parse(atob(payloadBase64));
            const exp = decodedPayload.exp;
            if (!exp) return;

            const expireTimeMs = exp * 1000;
            const now = Date.now();
            const timeoutDuration = expireTimeMs - now - 2000;

            if (timeoutDuration <= 0) {
                updateAccessToken();
            } else {
                if (accessTokenTimeout) clearTimeout(accessTokenTimeout);
                accessTokenTimeout = setTimeout(() => {
                    updateAccessToken();
                }, timeoutDuration);
            }
        } catch (err) {
            console.error("Errore nella decodifica del token:", err);
        }
    }

    async function updateAccessToken() {
        let request = await ApiInterface.refreshAccessToken();
        if (request.status !== 200) {
            Cookies.remove(environment.accessToken);
        } else {
            Cookies.remove(environment.accessToken);
            let json = await request.json();
            let token: AccessTokenResponse = new AccessTokenResponse(json);
            Cookies.set(environment.accessToken, token.accessToken, {expires: new Date(token.expireDate), path: '/'});
            setAccessTokenTimer()
        }
        return undefined;
    }

    async function refreshLoginToken() {
        const request = await ApiInterface.refreshAccessToken();

        if (request.status !== 200) {
            Cookies.remove(environment.accessToken);
            setLoadAccessToken("");
            return;
        }

        const json = await request.json();
        const token = new AccessTokenResponse(json);

        Cookies.set(environment.accessToken, token.accessToken, {
            expires: new Date(token.expireDate),
            path: '/',
            secure: true,
            sameSite: 'Lax'
        });
        setLoadAccessToken(token.accessToken);
        setAccessTokenTimer();
    }

    useEffect(() => {
        refreshLoginToken();
    }, []);

    useEffect(() => {
        if(loadAccessToken === null) return;

        checkCookies();
        loadInfo();
    }, [loadAccessToken]);

    return (
        <UserContext.Provider value={{
            isLogged,
            isStaff,
            userLoadingComplete,
            minecraftData,
            accountData,
            twitchData,
            reportsData,
            newRefreshToken
        }}>
            {children}
        </UserContext.Provider>
    );
};


async function getMinecraft(token: string | undefined) {
    if (token === null || token === undefined || token === "") {
        return null;
    }

    const result = fetch(environment.apiUrlOne + '/details/minecraft', {
        method: 'GET',
        headers: {
            'Authorization': token
        }
    })

    const rest = await result;
    return rest
}


export async function fetchTwitch(token: string | undefined) {
    if (token === null || token === undefined || token === "") {
        return null;
    }

    const result = fetch(environment.apiUrlOne + '/details/twitch', {
        method: 'GET',
        headers: {
            'Authorization': token
        }
    })

    const rest = await result;
    return rest
}

export async function fetchReports(token: string | undefined) {
    if (token === null || token === undefined || token === "") {
        return null;
    }

    const result = fetch(environment.apiUrlTwo + '/details/reports', {
        method: 'GET',
        headers: {
            'Authorization': token
        }
    })

    const rest = await result;
    return rest
}